import { useState, type CSSProperties } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button, Input, HelpAnchor } from '@/design-system';
import { cn } from '@/shared/cn';
import { AUTH_FIBER_PAGE } from '@/shared/ui-class';
import { AUTH_SCENE_GRID_CSS, FiberPaperBackdrop } from './AuthSplitShell';
import { BrandMark } from '@/versions/v1/shared/BrandMark'; // fork(oem): brand-aware wordmark

// Auth scene — the SSO landing. Real SSO is a backend integration; this scene
// is the *visible* landing for that flow — a UI-only mock. Composition:
//   • Two-column layout (desktop) — left: soft hero panel with the wordmark
//     + tagline. Right: the sign-in card.
//   • Sign-in card primary CTA: large accent "Sign in with SSO" button. On
//     click, simulates the redirect (~1.2s spinner) then lands the user at
//     the workspace home.
//   • Collapsed dev fallback below an "or" divider — opens the legacy
//     email/password form. Default state is collapsed so a demo doesn't
//     surface it; the dev workflow is preserved.
//   • Below ~768px the layout stacks single-column (hero on top).
//
// Does not change auth state. The persona-switch mechanism lives in TopBar /
// RoleContext and is unaffected — SSO just lands the user in the same place
// that the legacy form did.
export function AuthScene() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [phase, setPhase] = useState<'idle' | 'redirecting'>('idle');
  const [devOpen, setDevOpen] = useState(false);

  const reason = searchParams.get('reason');
  const error = searchParams.get('error');
  const blocked = searchParams.get('blocked') === '1';
  const sessionEnded = reason === 'session-ended' || reason === 'expired';
  const signInErrorMessage = signInErrorText(error);

  const enter = () => navigate('/');

  const handleSso = () => {
    if (phase !== 'idle') return;
    setPhase('redirecting');
    window.setTimeout(() => {
      enter();
    }, 1200);
  };

  return (
    <div
      data-comment-anchor="AuthScene"
      className={cn('relative w-full overflow-hidden', AUTH_FIBER_PAGE)}
    >
      <FiberPaperBackdrop />
      <div
        className="auth-scene-grid relative z-[1]"
        style={{
          display: 'grid',
          minHeight: '100vh',
          width: '100%',
        }}
      >
        {/* Left — hero panel. Wordmark + tagline on a soft surface tier. */}
        <aside
          aria-hidden
          className="auth-hero"
          style={{
            background: 'var(--surface-1)',
            borderRight: '1px solid var(--hairline)',
            padding: '48px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: 0,
          }}
        >
          <div className="flex items-center gap-3">
            <span
              style={{
                width: 12,
                height: 12,
                background: 'var(--accent)',
                borderRadius: 2,
                display: 'inline-block',
              }}
            />
            {/* fork(oem): was the literal "Foundation" */}
            <BrandMark variant="lockup" className="text-[22px]" logoHeight={24} />
          </div>

          <div style={{ maxWidth: 420 }}>
            <div
              style={{
                fontSize: 32,
                fontWeight: 600,
                lineHeight: 1.15,
                letterSpacing: '-0.01em',
                color: 'var(--ink-primary)',
                marginBottom: 12,
              }}
            >
              {/* fork(oem): was foundation boilerplate copy */}
              One view of the fleet, the work and what it costs.
            </div>
            <div
              style={{
                fontSize: 14,
                lineHeight: 1.55,
                color: 'var(--ink-secondary)',
              }}
            >
              Condition, predictive alerts, parts and cost pulled together across
              the systems that hold them — with an assistant that shows its sources.
            </div>
          </div>

          <div
            style={{
              fontSize: 11,
              letterSpacing: '0.04em',
              color: 'var(--ink-tertiary)',
            }}
          >
            {/* fork(oem) */}AiRis ProjeX OEM+ · proof of concept · v1
          </div>
        </aside>

        {/* Right — sign-in card. */}
        <main
          className="auth-form-col"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '48px 32px',
            minHeight: 0,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 380,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  color: 'var(--ink-primary)',
                  letterSpacing: '-0.01em',
                }}
              >
                Sign in
              </div>
              <HelpAnchor doc="workspace/home" />
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--ink-secondary)',
                marginBottom: 24,
              }}
            >
              Use your organisation account to access the workspace.{' '}
              <Link to="/signup" style={{ color: 'var(--accent)' }}>
                Sign up
              </Link>
            </div>

            {sessionEnded ? (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
                Your session ended — please sign in again.
              </div>
            ) : null}
            {signInErrorMessage && !sessionEnded ? (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200">
                {signInErrorMessage}
              </div>
            ) : null}
            {blocked ? (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200">
                This account is blocked. Contact{' '}
                <a href="mailto:admin@example.com">admin@example.com</a>.
              </div>
            ) : null}

            {/* Primary CTA — accent SSO button. */}
            <button
              type="button"
              onClick={handleSso}
              disabled={phase === 'redirecting'}
              aria-busy={phase === 'redirecting'}
              style={ssoButtonStyle(phase === 'redirecting')}
              onMouseEnter={(e) => {
                if (phase !== 'redirecting') {
                  e.currentTarget.style.filter = 'brightness(1.08)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.filter = 'none';
              }}
            >
              {phase === 'redirecting' ? (
                <>
                  <Loader2
                    size={16}
                    aria-hidden
                    style={{ animation: 'spin 900ms linear infinite' }}
                  />
                  <span>Redirecting to sign-in…</span>
                </>
              ) : (
                <>
                  <Building2 size={16} aria-hidden />
                  <span>Sign in with SSO</span>
                </>
              )}
            </button>

            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              style={{
                marginTop: 12,
                fontSize: 12,
                color: 'var(--ink-tertiary)',
                textAlign: 'center',
                textDecoration: 'none',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--ink-secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--ink-tertiary)';
              }}
            >
              Trouble signing in? Contact IT
            </a>

            {/* "or" divider — faint hairline with centred label. */}
            <div
              role="separator"
              aria-label="or"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                margin: '28px 0 16px',
              }}
            >
              <span
                style={{
                  flex: 1,
                  height: 1,
                  background: 'var(--hairline)',
                }}
              />
              <span
                style={{
                  fontSize: 11,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--ink-tertiary)',
                }}
              >
                or
              </span>
              <span
                style={{
                  flex: 1,
                  height: 1,
                  background: 'var(--hairline)',
                }}
              />
            </div>

            {/* Developer fallback — collapsed by default. Opens the legacy
                email form so the dev workflow is preserved without leading
                the client demo. */}
            <button
              type="button"
              onClick={() => setDevOpen((v) => !v)}
              aria-expanded={devOpen}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                background: 'transparent',
                border: 'none',
                color: 'var(--ink-tertiary)',
                fontSize: 12,
                cursor: 'pointer',
                padding: '4px 8px',
                alignSelf: 'center',
                transition: 'color 120ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = 'var(--ink-secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--ink-tertiary)';
              }}
            >
              <span>Sign in with email</span>
              {devOpen ? (
                <ChevronUp size={12} aria-hidden />
              ) : (
                <ChevronDown size={12} aria-hidden />
              )}
            </button>

            {devOpen ? (
              <div
                style={{
                  marginTop: 14,
                  padding: '14px',
                  borderRadius: 10,
                  background: 'var(--surface-1)',
                  border: '1px solid var(--hairline)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <Input
                  type="email"
                  placeholder="name@example.com"
                  aria-label="Email"
                />
                <Input
                  type="password"
                  placeholder="Password"
                  aria-label="Password"
                />
                <Button
                  variant="ghost"
                  onClick={enter}
                  style={{
                    justifyContent: 'center',
                    width: '100%',
                  }}
                >
                  Sign in
                </Button>
                <div
                  style={{
                    fontSize: 10,
                    letterSpacing: '0.04em',
                    color: 'var(--ink-tertiary)',
                    textAlign: 'center',
                  }}
                >
                  Developer fallback · not exposed in production
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/login?error=oauth')}
                  style={{
                    marginTop: 8,
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--accent)',
                    fontSize: 11,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    alignSelf: 'center',
                  }}
                >
                  Simulate SSO error
                </button>
              </div>
            ) : null}

            <div
              style={{
                marginTop: 32,
                fontSize: 11,
                letterSpacing: '0.04em',
                color: 'var(--ink-tertiary)',
                textAlign: 'center',
              }}
            >
              Secured by Single Sign-On · v1 proof of concept
            </div>
          </div>
        </main>
      </div>

      {/* Local style block — handles the two-column → single-column flip at
          768px without adding to global.css (auth scene is the only consumer).
          Also hosts the spinner keyframe so we don't depend on a global one. */}
      <style>{authSceneCss + AUTH_SCENE_GRID_CSS}</style>
    </div>
  );
}

function signInErrorText(code: string | null): string | null {
  if (!code) return null;
  if (code === 'oauth') {
    return "We couldn't complete single sign-on. Try again or use the developer fallback.";
  }
  return `Sign-in failed: ${code}`;
}

function ssoButtonStyle(busy: boolean): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    width: '100%',
    height: 44,
    borderRadius: 8,
    background: 'var(--accent)',
    // The ink that pairs with --accent; both themes define it, and a brand can
    // override it in styles/brands.css. Was a literal #FFFFFF.
    color: 'var(--accent-text)',
    border: 'none',
    fontSize: 14,
    fontWeight: 500,
    fontFamily: 'inherit',
    cursor: busy ? 'progress' : 'pointer',
    opacity: busy ? 0.85 : 1,
    transition: 'filter 120ms ease, opacity 120ms ease',
    boxShadow:
      '0 1px 0 rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.05)',
  };
}

// CSS for the scene. Kept local because (a) the auth scene is the only
// consumer of the two-column flip + spin keyframe and (b) global.css already
// has no auth-related rules — no inheritance to worry about per the
// chrome-editing rule in CLAUDE.md.
const authSceneCss = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
